import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import IconAction from "@/components/IconAction";
import { AlertTriangle, CalendarOff, Plus, Trash2, FileText } from "lucide-react";
import { SettingsHeader, SettingsSaveBar } from "./_shared";
import { sortCountriesByPriority, flagEmoji, PRIORITY_COUNTRIES, HIDDEN_COUNTRIES } from "@/lib/countries";
import Flag from "@/components/Flag";

export default function LeavesSettings() {
  const [s, setS] = useState(null);
  const [maxLeave, setMaxLeave] = useState(18);
  const [maxLeaveCredit, setMaxLeaveCredit] = useState(18);
  const [saving, setSaving] = useState(false);

  const [holidays, setHolidays] = useState([]);
  const [holYear, setHolYear] = useState(new Date().getFullYear());
  const [holDraft, setHolDraft] = useState({ date: "", name: "", country: "" });
  const [holCountries, setHolCountries] = useState([]);
  const [holSelected, setHolSelected] = useState([]);
  const [holCountriesUsed, setHolCountriesUsed] = useState([]);
  const [holCountryFilter, setHolCountryFilter] = useState("__ALL__");
  const [showOnlyUsedCountries, setShowOnlyUsedCountries] = useState(true);
  const [importCountry, setImportCountry] = useState("MA");
  const [importOverwrite, setImportOverwrite] = useState(false);
  const [importing, setImporting] = useState(false);
  const [missingCountryMissions, setMissingCountryMissions] = useState([]);
  const [bulkCountry, setBulkCountry] = useState("MA");
  const [bulkApplying, setBulkApplying] = useState(false);

  // Iter-87: collapse the huge ISO list behind "Autre…" for the bulk picker, mirroring
  // the Clients/NewMission UX. Shorter lists (already filtered to mission-used countries)
  // use a simple priority sort without an expander.
  const [bulkShowAll, setBulkShowAll] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    const [r, h, hc, hcu, mc] = await Promise.all([
      api.get("/settings"),
      api.get(`/holidays?year=${holYear}`),
      api.get("/holidays/countries").catch(() => ({ data: [] })),
      api.get("/holidays/countries-used").catch(() => ({ data: [] })),
      api.get("/missions/missing-country").catch(() => ({ data: [] })),
    ]);
    setS(r.data);
    setHolidays(h.data || []);
    setHolCountries(hc.data || []);
    setHolCountriesUsed(hcu.data || []);
    setMissingCountryMissions(mc.data || []);
    setMaxLeave(r.data.max_paid_leave_days ?? 18);
    setMaxLeaveCredit(r.data.max_paid_leave_credit_days ?? 18);
    setHolSelected([]);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [holYear]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        max_paid_leave_days: parseInt(maxLeave, 10) || 0,
        max_paid_leave_credit_days: parseInt(maxLeaveCredit, 10) || 0,
      });
      toast.success("Leave policy saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const addHoliday = async () => {
    if (!holDraft.date || !holDraft.name.trim()) { toast.error("Date and label required"); return; }
    try {
      await api.post("/holidays", { date: holDraft.date, name: holDraft.name, country: holDraft.country || null });
      toast.success("Public holiday added");
      setHolDraft({ date: "", name: "", country: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const removeHoliday = async (h) => {
    if (!window.confirm(`Delete ${h.date} — ${h.name} ?`)) return;
    try { await api.delete(`/holidays/${h.holiday_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const bulkDeleteHolidays = async () => {
    if (holSelected.length === 0) return;
    if (!window.confirm(`Delete ${holSelected.length} jour(s) férié(s) sélectionné(s) ? Action irréversible.`)) return;
    try {
      const r = await api.post("/holidays/bulk-delete", { holiday_ids: holSelected });
      toast.success(`${r.data.deleted} public holiday(s) deleted`);
      setHolSelected([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const toggleHolSelect = (id) => setHolSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleAllVisibleHol = (visible) => {
    const ids = visible.map((h) => h.holiday_id);
    const allSelected = ids.every((id) => holSelected.includes(id));
    setHolSelected(allSelected ? holSelected.filter((id) => !ids.includes(id)) : Array.from(new Set([...holSelected, ...ids])));
  };
  const importHolidays = async () => {
    if (!importCountry) return toast.error("Choisissez un pays");
    setImporting(true);
    try {
      const r = await api.post("/holidays/import", { country: importCountry, year: holYear, overwrite: importOverwrite });
      toast.success(`${r.data.created} public holiday(s) imported · ${r.data.skipped} already present`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import error"); }
    finally { setImporting(false); }
  };
  const bulkSetCountry = async () => {
    if (!bulkCountry) return;
    const count = missingCountryMissions.length;
    if (!window.confirm(`Assigner le pays ${bulkCountry} à ${count} mission(s) sans pays ? Reversible via individual editing.`)) return;
    setBulkApplying(true);
    try {
      const r = await api.post("/missions/bulk-set-country", { country: bulkCountry });
      toast.success(`${r.data.updated} mission(s) updated with ${r.data.country}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBulkApplying(false); }
  };

  if (!s) return <div className="p-12 text-zinc-500">Loading…</div>;
  const visibleHol = holidays.filter((h) => {
    if (holCountryFilter === "__ALL__") return true;
    if (holCountryFilter === "__GLOBAL__") return !h.country;
    return h.country === holCountryFilter;
  });

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[900px]" data-testid="settings-page-leaves">
      <SettingsHeader
        kicker="Leaves"
        title="Leaves & public holidays"
        description="Annual quota, carry-over credit and public holiday management."
      />

      {/* Leave policy */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-4 mb-6" data-testid="leave-policy-block">
        <div className="flex items-center gap-3 text-zinc-900">
          <FileText size={18} className="text-sky-600" />
          <h2 className="font-heading font-bold text-lg">Leave policy</h2>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl">
          Annual quota for internal profiles (CEO / Manager / Gestionnaire / Collaborator — freelance excluded).
          Days logged above the quota are marked red on the timesheet and counted as credit
          for next year, up to the limit defined below. Beyond <strong>quota + credit</strong>,
          leave entries are refused. Leaves cannot be set on weekends or public holidays.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Annual leave quota (days)</Label>
            <Input type="number" min={0} max={365} value={maxLeave} onChange={(e) => setMaxLeave(e.target.value)} data-testid="settings-max-leave" />
          </div>
          <div>
            <Label>Max carry-over credit (next year)</Label>
            <Input type="number" min={0} max={365} value={maxLeaveCredit} onChange={(e) => setMaxLeaveCredit(e.target.value)} data-testid="settings-max-leave-credit" />
          </div>
        </div>
      </div>

      {/* Missing-country banner */}
      {missingCountryMissions.length > 0 && (
        <div data-testid="missions-missing-country-banner" className="mb-6 bg-amber-50 border border-amber-300 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-amber-900 mb-1">
                {missingCountryMissions.length} mission{missingCountryMissions.length > 1 ? "s" : ""} sans pays défini
              </div>
              <p className="text-xs text-amber-900/80 max-w-2xl mb-4">
                Les collaborateurs affectés à ces missions ne voient pas de jours fériés nationaux sur leur timesheet.
                Assignez-leur un pays en bloc (vous pourrez toujours corriger individuellement via l'édition de chaque mission).
              </p>
              <div className="bg-white/60 border border-amber-200 rounded-md p-3 mb-4 max-h-32 overflow-y-auto">
                <div className="flex flex-wrap gap-1.5">
                  {missingCountryMissions.slice(0, 12).map((m) => (
                    <span key={m.mission_id} data-testid={`missing-country-mission-${m.mission_id}`} className="inline-flex items-center text-[11px] px-2 py-0.5 bg-white border border-amber-200 rounded font-mono text-amber-900">
                      {m.title}
                    </span>
                  ))}
                  {missingCountryMissions.length > 12 && (
                    <span className="inline-flex items-center text-[11px] px-2 py-0.5 text-amber-700 font-mono">
                      + {missingCountryMissions.length - 12} other{missingCountryMissions.length - 12 > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px]">
                  <Label className="mb-2 block text-xs text-amber-900">Country to assign to all</Label>
                  <Select
                    value={bulkCountry}
                    open={bulkOpen}
                    onOpenChange={(o) => { setBulkOpen(o); if (!o) setBulkShowAll(false); }}
                    onValueChange={setBulkCountry}
                  >
                    <SelectTrigger data-testid="bulk-country-select" className="h-10 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[320px]">
                      {(() => {
                        const byCode = Object.fromEntries((holCountries || []).map((c) => [c.code, c]));
                        const priority = PRIORITY_COUNTRIES.map((code) => byCode[code]).filter(Boolean);
                        const rest = (holCountries || [])
                          .filter((c) => !PRIORITY_COUNTRIES.includes(c.code) && !HIDDEN_COUNTRIES.has(c.code))
                          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                        return (
                          <>
                            {priority.map((c) => (
                              <SelectItem key={c.code} value={c.code} data-testid={`bulk-country-opt-${c.code}`}>
                                <span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={14} alt={false} /> {c.name} ({c.code})</span>
                              </SelectItem>
                            ))}
                            {!bulkShowAll && (
                              <button
                                type="button"
                                data-testid="bulk-country-show-more"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBulkShowAll(true); }}
                                onPointerDown={(e) => e.stopPropagation()}
                                className="w-full text-left py-1.5 pl-8 pr-2 text-sm text-zinc-500 font-medium hover:bg-zinc-100 rounded-sm focus:outline-none"
                              >
                                Autre…
                              </button>
                            )}
                            {bulkShowAll && rest.map((c) => (
                              <SelectItem key={c.code} value={c.code} data-testid={`bulk-country-opt-${c.code}`}>
                                <span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={14} alt={false} /> {c.name} ({c.code})</span>
                              </SelectItem>
                            ))}
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <button onClick={bulkSetCountry} disabled={bulkApplying} data-testid="bulk-country-apply" className="inline-flex items-center gap-2 px-4 h-10 bg-amber-700 text-white text-sm font-semibold rounded-md hover:bg-amber-800 disabled:opacity-50">
                  {bulkApplying ? "Application…" : `Assign to ${missingCountryMissions.length} mission${missingCountryMissions.length > 1 ? "s" : ""}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Holidays */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6">
        <div className="flex items-center gap-3 text-zinc-900">
          <CalendarOff size={18} className="text-zinc-700" />
          <h2 className="font-heading font-bold text-lg">Public holidays</h2>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl">Public holidays and weekends are grayed out on timesheets. When a user logs hours on a holiday or weekend, an on-call reason is required at submission. Each public holiday can be <strong>global</strong> or attached to a <strong>pays</strong>.</p>

        <div className="bg-brand-cream/40 border border-brand-navy/15 rounded-md p-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-brand-navy-500 font-mono mb-3">Import a national calendar</div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="mb-2 block">Year</Label>
              <Input type="number" min={2020} max={2050} value={holYear} onChange={(e) => setHolYear(parseInt(e.target.value, 10) || new Date().getFullYear())} className="w-24" data-testid="holidays-year" />
            </div>
            <div className="min-w-[220px]">
              <Label className="mb-2 block">Country</Label>
              <Select value={importCountry} onValueChange={setImportCountry}>
                <SelectTrigger data-testid="holiday-import-country" className="h-10"><SelectValue placeholder="Select a country" /></SelectTrigger>
                <SelectContent>
                  {/* Iter-84: filter to mission-used countries only. Iter-87: pinned MA/SN/FR first + flags. */}
                  {sortCountriesByPriority(holCountriesUsed.length > 0 ? holCountriesUsed : holCountries).map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`holiday-import-opt-${c.code}`}><span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={14} alt={false} /> {c.name} ({c.code})</span></SelectItem>
                  ))}
                  {holCountriesUsed.length === 0 && (
                    <div className="px-3 py-2 text-xs text-zinc-500 italic">No mission has a country yet — assign one in Missions to populate this list.</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 h-10 text-xs text-zinc-600 cursor-pointer select-none">
              <input type="checkbox" checked={importOverwrite} onChange={(e) => setImportOverwrite(e.target.checked)} data-testid="holiday-import-overwrite" />
              Overwrite existing days of that country for the year
            </label>
            <button onClick={importHolidays} disabled={importing} data-testid="holiday-import-submit" className="inline-flex items-center gap-2 px-4 h-10 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600 disabled:opacity-50">
              <Plus size={14} /> {importing ? "Importing…" : "Import"}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="mb-2 block">Date</Label>
            <Input type="date" value={holDraft.date} onChange={(e) => setHolDraft({ ...holDraft, date: e.target.value })} data-testid="holiday-date" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="mb-2 block">Label</Label>
            <Input value={holDraft.name} onChange={(e) => setHolDraft({ ...holDraft, name: e.target.value })} placeholder="e.g. Aïd Al Fitr" data-testid="holiday-name" />
          </div>
          <div className="min-w-[220px]">
            <Label className="mb-2 block">Country (optional)</Label>
            <Select value={holDraft.country || "__GLOBAL__"} onValueChange={(v) => setHolDraft({ ...holDraft, country: v === "__GLOBAL__" ? "" : v })}>
              <SelectTrigger data-testid="holiday-manual-country" className="h-10" title="Global = visible par tous"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__GLOBAL__" data-testid="holiday-manual-opt-global">— Global (all countries) —</SelectItem>
                {/* Iter-87: filter to mission-used countries + priority sort + flags. */}
                {sortCountriesByPriority(holCountriesUsed.length > 0 ? holCountriesUsed : holCountries).map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`holiday-manual-opt-${c.code}`}><span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={14} alt={false} /> {c.name} ({c.code})</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button onClick={addHoliday} data-testid="holiday-add" className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800">
            <Plus size={14} /> Add
          </button>
        </div>

        <div className="border border-zinc-200 rounded-md overflow-x-auto md:overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-b border-zinc-200 bg-zinc-50 text-xs">
            <label className="inline-flex items-center gap-1.5 text-zinc-700 cursor-pointer select-none">
              <input type="checkbox" checked={showOnlyUsedCountries} onChange={(e) => setShowOnlyUsedCountries(e.target.checked)} data-testid="holidays-only-used-toggle" />
              Only active mission countries
            </label>
            <div className="w-44">
              <Select value={holCountryFilter} onValueChange={setHolCountryFilter}>
                <SelectTrigger data-testid="holidays-country-filter" className="h-8 text-xs"><SelectValue placeholder="Filter by country" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__" data-testid="holidays-country-filter-all">All (global + per-country)</SelectItem>
                  <SelectItem value="__GLOBAL__" data-testid="holidays-country-filter-global">Global only</SelectItem>
                  {sortCountriesByPriority(showOnlyUsedCountries ? holCountriesUsed : holCountries).map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`holidays-country-filter-${c.code}`}><span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={14} alt={false} /> {c.name} ({c.code})</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-zinc-500 font-mono">{holSelected.length} sélectionné(s)</span>
              <button onClick={bulkDeleteHolidays} disabled={holSelected.length === 0} data-testid="holidays-bulk-delete" className="inline-flex items-center gap-1.5 px-3 h-8 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-40">
                <Trash2 size={12} /> Delete selection
              </button>
            </div>
          </div>
          <div className="grid grid-cols-12 min-w-[760px] px-4 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono bg-zinc-50 border-b border-zinc-200 items-center">
            <div className="col-span-1">
              <input type="checkbox" checked={visibleHol.length > 0 && visibleHol.every((h) => holSelected.includes(h.holiday_id))} onChange={() => toggleAllVisibleHol(visibleHol)} data-testid="holidays-select-all" />
            </div>
            <div className="col-span-2">Date</div>
            <div className="col-span-7">Label</div>
            <div className="col-span-1">Country</div>
            <div className="col-span-1 text-right">—</div>
          </div>
          {visibleHol.map((h) => (
            <div key={h.holiday_id} className="flex items-center gap-3 md:grid md:grid-cols-12 md:items-center md:min-w-[760px] md:gap-0 px-4 py-2 text-sm border-b border-zinc-100 last:border-b-0" data-testid={`holiday-row-${h.holiday_id}`}>
              <div className="col-span-1">
                <input type="checkbox" checked={holSelected.includes(h.holiday_id)} onChange={() => toggleHolSelect(h.holiday_id)} data-testid={`holiday-select-${h.holiday_id}`} />
              </div>
              <div className="col-span-2 font-mono text-zinc-700">{h.date}</div>
              <div className="col-span-7">{h.name}</div>
              <div className="col-span-1">
                {h.country ? (
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-brand-cream text-brand-navy font-mono rounded">
                    <Flag code={h.country} size={14} alt={false} />{h.country}
                  </span>
                ) : (
                  <span className="text-[10px] text-zinc-400 font-mono uppercase">global</span>
                )}
              </div>
              <div className="col-span-1 text-right">
                <IconAction icon={Trash2} tone="danger" testid={`holiday-delete-${h.holiday_id}`} label="Supprimer ce jour férié" onClick={() => removeHoliday(h)} />
              </div>
            </div>
          ))}
          {holidays.length === 0 && <div className="px-4 py-6 text-sm text-zinc-500 text-center">No holiday for {holYear}.</div>}
        </div>
      </div>

      <SettingsSaveBar onSave={save} onReset={load} saving={saving} testId="settings-save" />
    </div>
  );
}
